import mutationService, { MutationType, MutationEntity } from './mutationService'
import dexieService from './dexieService'
import apiService from './api'

/**
 * Fonctions helper pour simplifier l'utilisation du service de mutation
 * Ces fonctions encapsulent la logique commune pour différents types de mutations
 */

/**
 * Met à jour le gestionnaire technique d'une propriété
 * @param {string} propertyId - ID de la propriété
 * @param {string|null} managerId - ID du gestionnaire technique
 * @param {string|null} teamId - ID de l'équipe
 * @returns {Promise<{success: boolean, synced: boolean}>}
 */
export async function updatePropertyTechnicalManager(propertyId, managerId, teamId = null) {
  const data = {
    technicalManager: managerId ? { id: managerId } : null,
    team: teamId ? { id: teamId } : null,
    updatedAt: new Date().toISOString()
  }

  return await mutationService.executeMutation({
    entity: MutationEntity.PROPERTY_TECHNICAL_MANAGER,
    type: MutationType.UPDATE,
    entityId: propertyId,
    data,
    apiCall: async () => {
      return await apiService.request(`/properties/${propertyId}/technical-manager`, {
        method: 'PUT',
        body: JSON.stringify({
          technicalManagerId: managerId,
          teamId: teamId
        })
      })
    }
  })
}

/**
 * Met à jour la planification d'une propriété
 * @param {string} propertyId - ID de la propriété
 * @param {string|null} planifiedAt - Date de planification (ISO string)
 * @returns {Promise<{success: boolean, synced: boolean}>}
 */
export async function updatePropertyPlanification(propertyId, planifiedAt) {
  const data = {
    planifiedAt,
    updatedAt: new Date().toISOString()
  }

  return await mutationService.executeMutation({
    entity: MutationEntity.PROPERTY_PLANIFICATION,
    type: MutationType.UPDATE,
    entityId: propertyId,
    data,
    apiCall: async () => {
      return await apiService.request(`/properties/${propertyId}/planification`, {
        method: 'PUT',
        body: JSON.stringify({ planifiedAt })
      })
    }
  })
}

/**
 * Crée une nouvelle visite
 * @param {Object} visitData - Données de la visite
 * @returns {Promise<{success: boolean, synced: boolean, result?: Object}>}
 */
export async function createVisit(visitData) {
  // Générer un ID temporaire pour la visite si elle n'en a pas
  const tempId = visitData.id || `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
  
  const data = {
    ...visitData,
    tempId, // Stocker l'ID temporaire pour référence
    createdAt: new Date().toISOString()
  }

  const result = await mutationService.executeMutation({
    entity: MutationEntity.VISIT,
    type: MutationType.INSERT,
    entityId: null, // null car c'est une insertion
    data,
    apiCall: async () => {
      return await apiService.createVisit(visitData)
    },
    localUpdate: async (apiResult, toSync = false) => {
      // L'API retourne la propriété complète mise à jour (pas juste la visite!)
      // avec scheduledVisitAt calculé et le tableau visits mis à jour
      const propertyId = visitData.propertyId || visitData.property?.id
      
      if (apiResult && apiResult.id && apiResult.visits) {
        // apiResult est la propriété complète retournée par l'API
        // Mettre à jour la propriété complète dans IndexedDB
        // Cela met à jour automatiquement scheduledVisitAt, visits, etc.
        await dexieService.addProperty(apiResult)
        
        // IMPORTANT: Nettoyer les mutations de visite pour cette propriété
        // car la visite est maintenant dans property.visits (synchronisée)
        try {
          const { mutations } = await import('./dexieService')
          const pendingVisitMutations = await mutations()
            .filter(m => 
              m.entity === 'visit' && 
              (m.data?.propertyId === apiResult.id || m.data?.property?.id === apiResult.id)
            )
            .toArray()
          
          // Supprimer toutes les mutations de visite pour cette propriété
          for (const mutation of pendingVisitMutations) {
            await mutations().delete(mutation.id)
          }
        } catch (error) {
          console.warn('Erreur lors du nettoyage des mutations:', error)
        }
        
      } else if (toSync && propertyId) {
        // Mode hors ligne : créer la visite localement dans property.visits
        const visitToStore = {
          ...data,
          id: tempId,
          synced: false,
          toSync: 1,
          tempId: tempId
        }
        
        // Mettre à jour la propriété pour ajouter la visite à son tableau
        try {
          const property = await dexieService.getProperty(propertyId)
          if (property) {
            // Initialiser le tableau visits s'il n'existe pas
            if (!property.visits) {
              property.visits = []
            }
            
            // Vérifier si la visite n'est pas déjà dans le tableau
            const existingVisitIndex = property.visits.findIndex(v => v.id === visitToStore.id)
            
            if (existingVisitIndex === -1) {
              // Ajouter la nouvelle visite au tableau
              property.visits.push(visitToStore)
            } else {
              // Mettre à jour la visite existante
              property.visits[existingVisitIndex] = visitToStore
            }
            
            // Sauvegarder la propriété mise à jour
            await dexieService.addProperty(property)
          } else {
            console.warn(`Propriété ${propertyId} non trouvée pour l'ajout de la visite`)
          }
        } catch (error) {
          console.error('Erreur lors de la mise à jour de la propriété avec la visite:', error)
        }
      }
      
      // Note: l'enregistrement dans la table mutations est déjà géré par mutationService.executeMutation
    }
  })

  return result
}

/**
 * Met à jour une visite existante
 * @param {string} visitId - ID de la visite
 * @param {Object} visitData - Données à mettre à jour
 * @returns {Promise<{success: boolean, synced: boolean}>}
 */
export async function updateVisit(visitId, visitData) {
  const data = {
    ...visitData,
    updatedAt: new Date().toISOString()
  }

  return await mutationService.executeMutation({
    entity: MutationEntity.VISIT,
    type: MutationType.UPDATE,
    entityId: visitId,
    data,
    apiCall: async () => {
      return await apiService.updateVisit(visitId, visitData)
    },
    localUpdate: async (apiResult, toSync = false) => {
      // Les visites sont stockées dans property.visits
      // Trouver la propriété contenant cette visite
      const propertyId = visitData.propertyId || visitData.property?.id
      if (!propertyId) {
        console.warn('Impossible de mettre à jour la visite: propertyId manquant')
        return
      }
      
      const property = await dexieService.getProperty(propertyId)
      if (!property || !property.visits) {
        console.warn(`Propriété ${propertyId} ou ses visites non trouvées`)
        return
      }
      
      const visitIndex = property.visits.findIndex(v => v.id === visitId)
      if (visitIndex === -1) {
        console.warn(`Visite ${visitId} non trouvée dans la propriété ${propertyId}`)
        return
      }

      const updatedVisit = {
        ...property.visits[visitIndex],
        ...visitData,
        updatedAt: new Date().toISOString()
      }

      if (toSync) {
        updatedVisit.toSync = 1
      } else {
        delete updatedVisit.toSync
      }

      property.visits[visitIndex] = updatedVisit
      await dexieService.addProperty(property)
    }
  })
}

/**
 * Supprime une visite
 * @param {string} visitId - ID de la visite
 * @returns {Promise<{success: boolean, synced: boolean}>}
 */
export async function deleteVisit(visitId) {
  return await mutationService.executeMutation({
    entity: MutationEntity.VISIT,
    type: MutationType.DELETE,
    entityId: visitId,
    data: { deletedAt: new Date().toISOString() },
    apiCall: async () => {
      return await apiService.request(`/visits/${visitId}`, {
        method: 'DELETE'
      })
    },
    localUpdate: async (apiResult, toSync = false) => {
      // Les visites sont stockées dans property.visits
      // Trouver toutes les propriétés et supprimer la visite
      const allProperties = await dexieService.getAllProperties()
      
      for (const property of allProperties) {
        if (property.visits && Array.isArray(property.visits)) {
          const visitIndex = property.visits.findIndex(v => v.id === visitId)
          
          if (visitIndex !== -1) {
            if (!toSync) {
              // Si synchronisé avec succès, supprimer de la base locale
              property.visits.splice(visitIndex, 1)
            } else {
              // Si non synchronisé, marquer comme à supprimer
              property.visits[visitIndex].toDelete = 1
              property.visits[visitIndex].toSync = 1
            }
            
            await dexieService.addProperty(property)
            break
          }
        }
      }
    }
  })
}

/**
 * Crée une nouvelle action
 * @param {Object} actionData - Données de l'action
 * @returns {Promise<{success: boolean, synced: boolean, result?: Object}>}
 */
export async function createAction(actionData) {
  const tempId = actionData.id || `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
  
  const data = {
    ...actionData,
    tempId,
    createdAt: new Date().toISOString()
  }

  return await mutationService.executeMutation({
    entity: MutationEntity.ACTION,
    type: MutationType.INSERT,
    entityId: null,
    data,
    apiCall: async () => {
      return await apiService.request('/actions', {
        method: 'POST',
        body: JSON.stringify(actionData)
      })
    },
    localUpdate: async (apiResult, toSync = false) => {
      // Les actions ne sont plus stockées localement
      // Elles sont gérées uniquement via l'API
      // La synchronisation mettra à jour les propriétés concernées
      if (!toSync) {
        // Action synchronisée avec succès - la propriété sera mise à jour via sync
      } else {
        // Action en attente de synchronisation
      }
    }
  })
}

/**
 * Met à jour une action existante
 * @param {string} actionId - ID de l'action
 * @param {Object} actionData - Données à mettre à jour
 * @returns {Promise<{success: boolean, synced: boolean}>}
 */
export async function updateAction(actionId, actionData) {
  const data = {
    ...actionData,
    updatedAt: new Date().toISOString()
  }

  return await mutationService.executeMutation({
    entity: MutationEntity.ACTION,
    type: MutationType.UPDATE,
    entityId: actionId,
    data,
    apiCall: async () => {
      return await apiService.request(`/actions/${actionId}`, {
        method: 'PUT',
        body: JSON.stringify(actionData)
      })
    },
    localUpdate: async () => {
      // Les actions ne sont plus stockées localement
      // Elles sont gérées uniquement via l'API
    }
  })
}

/**
 * Supprime une action
 * @param {string} actionId - ID de l'action
 * @returns {Promise<{success: boolean, synced: boolean}>}
 */
export async function deleteAction(actionId) {
  return await mutationService.executeMutation({
    entity: MutationEntity.ACTION,
    type: MutationType.DELETE,
    entityId: actionId,
    data: { deletedAt: new Date().toISOString() },
    apiCall: async () => {
      return await apiService.request(`/actions/${actionId}`, {
        method: 'DELETE'
      })
    },
    localUpdate: async () => {
      // Les actions ne sont plus stockées localement
      // Elles sont gérées uniquement via l'API
    }
  })
}

/**
 * Traite les attributions multiples (pour plusieurs propriétés)
 * @param {Array<string>} propertyIds - IDs des propriétés
 * @param {string} managerId - ID du gestionnaire
 * @param {string} teamId - ID de l'équipe
 * @returns {Promise<{success: boolean, results: Array, synced: boolean}>}
 */
export async function processMultipleAttributions(propertyIds, managerId, teamId) {
  const results = []
  let allSynced = true

  for (const propertyId of propertyIds) {
    try {
      const result = await updatePropertyTechnicalManager(propertyId, managerId, teamId)
      results.push({
        propertyId,
        success: result.success,
        synced: result.synced,
        error: result.error
      })

      if (!result.synced) {
        allSynced = false
      }
    } catch (error) {
      console.error(`Erreur lors du traitement de la propriété ${propertyId}:`, error)
      results.push({
        propertyId,
        success: false,
        synced: false,
        error: error.message
      })
      allSynced = false
    }
  }

  const successCount = results.filter(r => r.success).length

  return {
    success: successCount === propertyIds.length,
    results,
    synced: allSynced,
    successCount,
    totalCount: propertyIds.length
  }
}

/**
 * Crée un contrôle
 * @param {Object} controlData - Données du contrôle
 * @returns {Promise<{success: boolean, synced: boolean}>}
 */
export async function createControl(controlData) {
  const tempId = controlData.id || `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
  
  const data = {
    ...controlData,
    tempId,
    createdAt: new Date().toISOString()
  }

  return await mutationService.executeMutation({
    entity: MutationEntity.CONTROL,
    type: MutationType.INSERT,
    entityId: null,
    data,
    apiCall: async () => {
      return await apiService.request('/controls', {
        method: 'POST',
        body: JSON.stringify(controlData)
      })
    },
    localUpdate: async (apiResult, toSync = false) => {
      // Les contrôles sont stockés différemment dans dexieService
      const { controls } = await import('./dexieService')
      
      const controlToStore = {
        ...(apiResult || data),
        id: apiResult?.id || tempId,
        insertedAt: new Date().toISOString(),
        synced: !toSync
      }

      if (toSync) {
        controlToStore.toSync = 1
        controlToStore.tempId = tempId
      }

      await controls().put(controlToStore)
    }
  })
}

/**
 * Met à jour un contrôle
 * @param {string} controlId - ID du contrôle
 * @param {Object} controlData - Données à mettre à jour
 * @returns {Promise<{success: boolean, synced: boolean}>}
 */
export async function updateControl(controlId, controlData) {
  const data = {
    ...controlData,
    updatedAt: new Date().toISOString()
  }

  return await mutationService.executeMutation({
    entity: MutationEntity.CONTROL,
    type: MutationType.UPDATE,
    entityId: controlId,
    data,
    apiCall: async () => {
      return await apiService.request(`/controls/${controlId}`, {
        method: 'PUT',
        body: JSON.stringify(controlData)
      })
    },
    localUpdate: async (apiResult, toSync = false) => {
      const { controls } = await import('./dexieService')
      
      const control = await controls().get(controlId)
      if (!control) {
        throw new Error(`Contrôle ${controlId} non trouvé`)
      }

      const updatedControl = {
        ...control,
        ...controlData,
        updatedAt: new Date().toISOString()
      }

      if (toSync) {
        updatedControl.toSync = 1
      } else {
        delete updatedControl.toSync
      }

      await controls().put(updatedControl)
    }
  })
}

/**
 * Crée un commentaire
 * @param {Object} commentData - Données du commentaire
 * @returns {Promise<{success: boolean, synced: boolean}>}
 */
export async function createComment(commentData) {
  const tempId = commentData.id || `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
  
  const data = {
    ...commentData,
    tempId,
    createdAt: new Date().toISOString()
  }

  return await mutationService.executeMutation({
    entity: MutationEntity.COMMENT,
    type: MutationType.INSERT,
    entityId: null,
    data,
    apiCall: async () => {
      return await apiService.request('/comments', {
        method: 'POST',
        body: JSON.stringify(commentData)
      })
    },
    localUpdate: async (apiResult, toSync = false) => {
      // Mettre à jour le control dans IndexedDB avec le nouveau commentaire
      const controlId = commentData.controlId || commentData.control?.id
      
      if (!controlId) {
        return
      }

      try {
        const { controls } = await import('./dexieService')
        
        // Récupérer le control existant
        const control = await controls().get(controlId)
        
        if (!control) {
          return
        }

        // Initialiser le tableau comments s'il n'existe pas
        if (!control.comments) {
          control.comments = []
        }

        // Créer le commentaire à ajouter
        const commentToAdd = {
          id: apiResult?.id || tempId,
          text: data.text,
          author: data.author,
          createdAt: data.createdAt,
          picturesUrl: data.pictures || [],
          synced: !toSync
        }

        if (toSync) {
          commentToAdd.toSync = 1
          commentToAdd.tempId = tempId
        }

        // Ajouter le commentaire au control
        control.comments.push(commentToAdd)
        
        // Sauvegarder le control mis à jour
        await controls().put(control)
      } catch (error) {
        console.error('Erreur lors de la mise à jour locale du commentaire:', error)
      }
    }
  })
}

export default {
  updatePropertyTechnicalManager,
  updatePropertyPlanification,
  createVisit,
  updateVisit,
  deleteVisit,
  createAction,
  updateAction,
  deleteAction,
  createControl,
  updateControl,
  createComment,
  processMultipleAttributions
}
